import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { usePlan } from "@/contexts/PlanContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Sparkles, CreditCard, Calendar, RefreshCw, Check, Loader2, ShieldCheck, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function OrgSubscription() {
  const { plan, loading, refresh } = usePlan();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("PREMIUM");
  const [billing, setBilling] = useState("YEARLY");
  const [addons, setAddons] = useState({ storage_extra_gb: 0, integrations: 0, marketplace: false });
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pollingSession, setPollingSession] = useState(null);
  const [pollState, setPollState] = useState(null);

  // Load plans on mount
  useEffect(() => {
    axios.get(`${BACKEND}/api/platform/public/plans`).then(({ data }) => {
      // Only paid plans are purchasable from the in-portal Renew flow — free trials
      // are provisioned via the public trial signup, not Stripe checkout.
      const paidOnly = (data || []).filter((p) => !p.is_trial);
      setPlans(paidOnly);
      // preselect current plan if any AND it's paid; else fall back to PREMIUM
      const cur = (plan?.plan_code || "").toUpperCase();
      if (cur && paidOnly.find((p) => p.code === cur)) setSelectedPlan(cur);
      else if (paidOnly.find((p) => p.code === "PREMIUM")) setSelectedPlan("PREMIUM");
      else if (paidOnly[0]) setSelectedPlan(paidOnly[0].code);
      if (plan?.billing_cycle) setBilling(plan.billing_cycle);
    }).catch(() => {});
  }, [plan?.plan_code, plan?.billing_cycle]);

  // Live quote
  useEffect(() => {
    if (!plans.length) return;
    axios.post(`${BACKEND}/api/platform/public/quote`, {
      plan_code: selectedPlan, billing_cycle: billing, role_counts: {}, addons,
    }).then(({ data }) => setQuote(data)).catch(() => {});
  }, [selectedPlan, billing, addons, plans.length]);

  // Handle Stripe redirect back
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (sid && !pollingSession) {
      setPollingSession(sid);
    }
  }, [searchParams, pollingSession]);

  useEffect(() => {
    if (!pollingSession) return;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${BACKEND}/api/org/subscribe/status/${pollingSession}`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("cera_token") || localStorage.getItem("cera_token") || ""}` },
        });
        setPollState(data);
        if (data.payment_status === "paid") {
          clearInterval(interval);
          toast.success("Payment successful — pending Super Admin approval.");
          await refresh();
        } else if (attempts >= 30) {
          clearInterval(interval);
          toast.error("Timed out checking payment status. Refresh to try again.");
        }
      } catch (e) {
        if (attempts >= 30) clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [pollingSession, refresh]);

  const planObj = useMemo(() => plans.find((p) => p.code === selectedPlan), [plans, selectedPlan]);

  const startCheckout = async () => {
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${BACKEND}/api/org/subscribe/checkout`, {
        plan_code: selectedPlan,
        billing_cycle: billing,
        addons,
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${sessionStorage.getItem("cera_token") || localStorage.getItem("cera_token") || ""}` } });
      toast.success("Redirecting to secure Stripe checkout…");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    } finally {
      setSubmitting(false);
    }
  };

  const clearReturnState = () => {
    setPollingSession(null);
    setPollState(null);
    searchParams.delete("session_id");
    setSearchParams(searchParams, { replace: true });
  };

  if (loading || !plan) return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="org-subscription-page">
      <div className="surface-card p-8 text-center text-muted-foreground">Loading subscription…</div>
    </div>
  );

  const status = plan.status || "NONE";
  const isActive = status === "ACTIVE" || status === "TRIALING";
  const isExpiring = plan.days_remaining !== undefined && plan.days_remaining <= 30;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="org-subscription-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-3"><CreditCard className="w-7 h-7" /> Subscription</h1>
        <p className="text-muted-foreground">Current plan, renewal timeline, and quick actions.</p>
      </div>

      {/* Stripe return banner */}
      {pollingSession ? (
        <div className="surface-card p-5 border-emerald-300/50 bg-emerald-500/5" data-testid="checkout-return-banner">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-display font-black text-lg">
                {pollState?.payment_status === "paid" ? "Payment successful" : "Confirming payment…"}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {pollState?.payment_status === "paid" ? (
                  <>Your subscription <strong>{pollState.plan_code}</strong> is now <Badge variant="outline" className="ml-1">{pollState.subscription_status}</Badge>. It will activate once a Super Admin approves the contract.</>
                ) : (
                  <>Stripe session: <span className="font-mono text-xs">{pollingSession.slice(0, 24)}…</span></>
                )}
              </div>
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={clearReturnState} data-testid="clear-checkout-return-btn">Dismiss</Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Current plan summary */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="surface-card p-6 lg:col-span-2 space-y-4" data-testid="current-plan-card">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`font-mono text-[10px] ${isActive ? "bg-emerald-50 text-emerald-900 border-emerald-200" : "bg-amber-50 text-amber-900 border-amber-200"}`}>{status}</Badge>
            {isExpiring ? <Badge variant="outline" className="font-mono text-[10px] bg-red-50 text-red-900 border-red-200">Expires in {plan.days_remaining}d</Badge> : null}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold">Current plan</div>
            <div className="font-display text-3xl sm:text-4xl font-black mt-1">{plan.plan_name || plan.plan_code || "No active plan"}</div>
            {plan.plan_tagline ? <div className="text-muted-foreground mt-1">{plan.plan_tagline}</div> : null}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Billing cycle</div>
              <div className="font-display font-black mt-1">{plan.billing_cycle || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Days remaining</div>
              <div className="font-display font-black mt-1">{plan.days_remaining ?? "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ends on</div>
              <div className="font-mono text-xs mt-2">{plan.current_period_ends ? new Date(plan.current_period_ends).toLocaleDateString() : "—"}</div>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => refresh()} data-testid="refresh-plan-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
            <a href="#renew" data-testid="scroll-to-renew">
              <Button className="bg-gradient-to-r from-orange-500 to-pink-500 text-white"><Sparkles className="w-4 h-4 mr-1" /> Renew / Change plan below</Button>
            </a>
          </div>
        </div>

        <div className="surface-card p-6" data-testid="plan-roles-card">
          <h3 className="font-display font-black text-lg">Roles included</h3>
          <p className="text-xs text-muted-foreground mb-3">{(plan.enabled_roles || []).length} roles in this plan</p>
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {(plan.enabled_roles || []).slice(0, 20).map((r) => (
              <div key={r.code} className="flex items-center justify-between text-xs py-1 border-b border-border/60 last:border-0">
                <span>{r.name}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{r.used}/{r.max}</span>
              </div>
            ))}
            {(plan.enabled_roles || []).length > 20 ? <div className="text-[11px] text-muted-foreground text-center pt-2">+{plan.enabled_roles.length - 20} more…</div> : null}
          </div>
        </div>
      </div>

      <div className="surface-card p-6" data-testid="plan-modules-card">
        <h3 className="font-display font-black text-lg mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Modules unlocked by this plan</h3>
        <div className="flex flex-wrap gap-2">
          {(plan.enabled_modules || []).map((m) => (
            <Badge key={m} variant="outline" className="font-mono text-[11px]">{m.toUpperCase()}</Badge>
          ))}
          {(plan.enabled_modules || []).length === 0 ? <span className="text-sm text-muted-foreground">No modules yet — purchase a plan below.</span> : null}
        </div>
      </div>

      {/* Renew / Upgrade — inline flow */}
      <div id="renew" className="surface-card p-6 space-y-5" data-testid="renew-section">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl font-black flex items-center gap-2"><Sparkles className="w-5 h-5 text-orange-500" /> Renew or Change your plan</h2>
            <p className="text-muted-foreground text-sm mt-1">Pay securely with Stripe. Your plan activates after Super Admin approves the contract.</p>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={billing === "YEARLY"} onCheckedChange={(v) => setBilling(v ? "YEARLY" : "MONTHLY")} data-testid="renew-billing-toggle" />
            <span className="text-sm">Yearly billing <span className="text-xs text-emerald-600 font-semibold">(2 months free)</span></span>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid sm:grid-cols-3 gap-3">
          {plans.map((p) => {
            const isSel = selectedPlan === p.code;
            const isCurrent = (plan?.plan_code || "").toUpperCase() === p.code;
            return (
              <button
                key={p.code}
                onClick={() => setSelectedPlan(p.code)}
                className={`relative text-left p-5 rounded-2xl border transition-all ${isSel ? "border-orange-500 bg-orange-50 shadow-lg" : "border-border bg-card hover:border-orange-300"}`}
                data-testid={`renew-plan-card-${p.code}`}
              >
                {p.highlight ? <span className="absolute top-3 right-3 text-[9px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-orange-500 text-white">POPULAR</span> : null}
                {isCurrent ? <span className="absolute top-3 left-3 text-[9px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-emerald-500 text-white">CURRENT</span> : null}
                <div className="font-display text-2xl font-black mt-4">{p.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{p.tagline}</div>
                <div className="mt-4 text-3xl font-black">
                  ₹{(billing === "YEARLY" ? p.base_price_yearly : p.base_price_monthly).toLocaleString()}
                  <span className="text-sm font-normal text-muted-foreground">/{billing === "YEARLY" ? "yr" : "mo"}</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">{p.storage_gb} GB · up to {p.max_users_default} users</div>
                {p.features?.length ? (
                  <ul className="mt-3 space-y-1">
                    {p.features.slice(0, 4).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </button>
            );
          })}
        </div>

        {/* Add-ons */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold mb-3">Add-ons (optional)</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="flex items-center justify-between p-3 rounded-xl border border-border">
              <div>
                <div className="text-sm font-semibold">Extra storage</div>
                <div className="text-[11px] text-muted-foreground">₹5 / GB / mo</div>
              </div>
              <Input
                type="number" min={0} value={addons.storage_extra_gb}
                onChange={(e) => setAddons({ ...addons, storage_extra_gb: parseInt(e.target.value || "0") })}
                className="w-20" data-testid="renew-addon-storage"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl border border-border">
              <div>
                <div className="text-sm font-semibold">Integrations</div>
                <div className="text-[11px] text-muted-foreground">₹999 / integration / mo</div>
              </div>
              <Input
                type="number" min={0} value={addons.integrations}
                onChange={(e) => setAddons({ ...addons, integrations: parseInt(e.target.value || "0") })}
                className="w-20" data-testid="renew-addon-integrations"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl border border-border">
              <div>
                <div className="text-sm font-semibold">Marketplace</div>
                <div className="text-[11px] text-muted-foreground">₹1,499 / mo</div>
              </div>
              <Switch checked={!!addons.marketplace} onCheckedChange={(v) => setAddons({ ...addons, marketplace: v })} data-testid="renew-addon-marketplace" />
            </div>
          </div>
        </div>

        {/* Quote + CTA */}
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-border p-5 bg-muted/30" data-testid="renew-quote-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-bold">Your quote</span>
            </div>
            {!quote ? <div className="text-sm text-muted-foreground">Calculating…</div> : (
              <div className="space-y-1.5">
                {quote.breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between text-sm" data-testid={`renew-quote-line-${i}`}>
                    <span className="text-muted-foreground">{b.label}</span>
                    <span className={`font-mono ${b.amount < 0 ? "text-emerald-600" : ""}`}>₹{b.amount.toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t border-border my-3" />
                <div className="flex justify-between items-center" data-testid="renew-quote-total">
                  <span className="font-semibold">Total</span>
                  <span className="font-display text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                    ₹{quote.total.toLocaleString()}
                    <span className="text-sm text-muted-foreground font-normal">/{billing === "YEARLY" ? "yr" : "mo"}</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border p-5 space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold">Ready to renew?</div>
              <div className="font-display text-xl font-black mt-1">{planObj?.name || "—"} · {billing === "YEARLY" ? "Yearly" : "Monthly"}</div>
              <p className="text-xs text-muted-foreground mt-1">Charged to <span className="font-mono">{planObj ? "your organization" : ""}</span>. Contract PDF is generated upon Super Admin approval.</p>
            </div>
            <Button
              onClick={startCheckout}
              disabled={submitting || !quote}
              size="lg"
              className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90 text-white font-bold rounded-xl"
              data-testid="pay-stripe-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Pay ₹{quote?.total?.toLocaleString() || "—"} · Stripe secure checkout
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">You’ll be redirected to Stripe and returned here after payment.</p>
          </div>
        </div>
      </div>

      <div className="surface-card p-6 bg-gradient-to-r from-orange-500/5 to-pink-500/5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-display font-black text-lg">Need help choosing a plan?</h3>
            <p className="text-muted-foreground text-sm mt-1">Contact your Super Admin or view the full plan comparison.</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/portals/organization#subscription-builder")} data-testid="compare-plans-btn">
            <ArrowUpRight className="w-4 h-4 mr-1" /> Full comparison
          </Button>
        </div>
      </div>
    </div>
  );
}
